import {
  isEmptySquadDynamicValue,
  isSquadDynamicCell,
  SQUAD_DYNAMIC_FONDSDATEN_KEYS,
} from './squadFondsdatenDynamic';
import { squadLabeledKeys } from './squadLabels';
import { collectSquadRiskDynamicCells, normalizePerformanceRiskTable } from './performanceRiskTable';
import { SQUAD_DYNAMIC_GEBUEHREN_KEYS } from './squadGebuehrenDynamic';
import { SQUAD_FONDSDATEN_KEYS, SQUAD_WEITERE_DATEN_KEYS } from './templateConstants';

/** Label shown when the SRRI could not be fetched (the box keeps the stored level). */
export const SQUAD_MISSING_SRRI_LABEL = 'Risikoindikator';
/** Label shown when not one figure of the Wertentwicklung/Risikokennzahlen table came back. */
export const SQUAD_MISSING_RISK_TABLE_LABEL = 'Wertentwicklung und Risikokennzahlen';

/**
 * Dynamic values that came back empty. A dynamic Fondsdaten row without a value is left
 * out of the PDF without a trace (filterSquadKvRows); this list is how the editor learns.
 */
export function listMissingSquadDynamicValues(
  data: any,
  fondsdatenCache: Map<string, string>,
  srri: number | null,
  riskTableValues?: Map<string, string>,
  gebuehrenValues?: Map<string, string>,
): string[] {
  const labels = new Map(
    squadLabeledKeys(SQUAD_FONDSDATEN_KEYS, data?.fondsdatenLabels).map(({ key, label }) => [key, label]),
  );
  const missing: string[] = [];
  for (const key of SQUAD_DYNAMIC_FONDSDATEN_KEYS) {
    const cell = data?.fondsdaten?.[key];
    if (!isSquadDynamicCell(cell) || !cell.fields?.length) continue;
    if (isEmptySquadDynamicValue(fondsdatenCache.get(key), cell)) missing.push(labels.get(key) || key);
  }
  if (gebuehrenValues) {
    const feeLabels = new Map(
      squadLabeledKeys(SQUAD_WEITERE_DATEN_KEYS, data?.weitereDatenLabels).map(({ key, label }) => [key, label]),
    );
    for (const key of SQUAD_DYNAMIC_GEBUEHREN_KEYS) {
      if (!isSquadDynamicCell(data?.weitereDaten?.[key])) continue;
      if (!String(gebuehrenValues.get(key) ?? '').trim()) missing.push(feeLabels.get(key) || key);
    }
  }
  if (srri == null) missing.push(SQUAD_MISSING_SRRI_LABEL);
  // Single empty cells are normal (a young class has no 10-year figure); an empty table is not.
  if (riskTableValues) {
    const cells = collectSquadRiskDynamicCells(normalizePerformanceRiskTable(data?.performanceRiskTable));
    if (cells.length && cells.every(({ key }) => !String(riskTableValues.get(key) ?? '').trim())) {
      missing.push(SQUAD_MISSING_RISK_TABLE_LABEL);
    }
  }
  return missing;
}

/** One sentence for the PDF dialog, or null when nothing is missing. */
export function squadMissingDynamicWarning(
  bundle: { isin: string; until?: string; missing: string[] } | null,
): string | null {
  if (!bundle || !bundle.missing.length) return null;
  const iso = String(bundle.until || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const asOf = iso ? ` zum ${iso[3]}.${iso[2]}.${iso[1]}` : '';
  return `Für ${bundle.isin}${asOf} fehlen in den Daten: ${bundle.missing.join(', ')}. Bitte vor dem Veröffentlichen prüfen.`;
}
